// Deterministic commercial-safety check shared by Stage 4 navigation, its
// visible warning, and the QuoteMaker Assistant readiness tool.
//
// The pricing ledger, the manual price authority and the generated-amount
// floor it reads moved to backend/src/shared/pricing-core/pricing-ledger.ts
// (Quote Brain slice PC). They are re-exported here, so callers keep this path.

import {
    getExplicitAllowanceAmount,
    requiresInclusionPricingRevalidation,
    type InclusionEntry,
} from './inclusions-helpers';
import {
    buildStage4PricingLedger,
    costBreakdownTotal,
    itemAmount,
    roundCents,
    type ActualCostCoverage,
    type Stage4PricingLedger,
} from '../../backend/src/shared/pricing-core/pricing-ledger.js';

export {
    MATERIAL_SHORTFALL_ABS,
    MATERIAL_SHORTFALL_PCT,
    buildStage4PricingLedger,
    hasReviewableManualAmountAuthority,
    hasReviewableManualCostAuthority,
    hasReviewableManualPriceAuthority,
    isManagedZeroCostReceipt,
    readMethodologyBridgeStamps,
    reconcileGeneratedAmountsToPricingFloor,
} from '../../backend/src/shared/pricing-core/pricing-ledger.js';
export type {
    ActualCostCoverage,
    MethodologyBridgeStamps,
    Stage4PricingLedger,
    Stage4PricingLedgerItem,
} from '../../backend/src/shared/pricing-core/pricing-ledger.js';

type UnknownRecord = Record<string, any>;

export interface QuotePricingReadinessInput {
    scope?: unknown;
    projectWMS?: unknown;
    isGstInclusive?: boolean;
}

export interface QuotePricingReadiness {
    quoteRevenueExGst: number;
    /** Canonical Stage 4 release floor. */
    pricingFloorExGst: number;
    pricingFloorSource: 'stage4-scope-ledger' | 'none';
    rateBookChargeOutIncGst: number;
    rateBookChargeOutExGst: number;
    unmatchedCostBaseExGst: number;
    pricingHeadroom: number;
    underpricedItemCount: number;
    pricingLedger: Stage4PricingLedger;
    /**
     * Actual builder margin is unknowable while rate-book lines have no
     * cost-to-business (`costRate`) data. These fields deliberately stay null
     * rather than presenting charge-out as cost.
     */
    actualBuilderCostExGst: number | null;
    actualMarginAmount: number | null;
    actualMarginPercent: number | null;
    actualCostCoverage: ActualCostCoverage;
    // Backward-compatible fields. `scopeCostExGst` and `knownCostExGst` now
    // alias the canonical pricing floor; `marginAmount`/`marginPercent` are
    // pricing-floor headroom, not claims about actual business margin.
    workMethodCostExGst: number;
    scopeCostExGst: number;
    knownCostExGst: number;
    costSource: 'work-method' | 'scope' | 'none';
    marginAmount: number;
    marginPercent: number | null;
    shortfall: number;
    hasKnownCost: boolean;
    lossMaking: boolean;
    pendingInclusionPricingReviewCount: number;
    pendingInclusionPricingReview: boolean;
    overAllocatedAllowanceParentCount: number;
    overAllocatedAllowanceExcess: number;
    overAllocatedAllowance: boolean;
    blocked: boolean;
}

/**
 * How far one scope item's explicit allowance amounts exceed its own price, in
 * the item's display basis, rounded to cents. 0 when they fit. This is the
 * per-item test behind `overAllocatedAllowance`, shared with the Stage 4 row
 * signals. Legacy section subtotals are not allowances and are not counted.
 */
export function allowanceExcessForItem(item: unknown): number {
    const record = (item && typeof item === 'object' ? item : {}) as UnknownRecord;
    const parentDisplayAmount = roundCents(Math.max(0, itemAmount(record)));
    const allowanceDisplayAmount = roundCents(Array.isArray(record?.inclusions)
        ? record.inclusions.reduce((sum: number, inclusion: InclusionEntry) => (
            sum + (getExplicitAllowanceAmount(inclusion) ?? 0)
        ), 0)
        : 0);
    if (allowanceDisplayAmount <= parentDisplayAmount) return 0;
    return roundCents(allowanceDisplayAmount - parentDisplayAmount);
}

function coreWorkMethodActivities(projectWMS: unknown): UnknownRecord[] {
    const record = projectWMS && typeof projectWMS === 'object'
        ? projectWMS as UnknownRecord
        : {};
    const activities = record?.structured?.activities;
    if (!Array.isArray(activities)) return [];
    return activities.filter(activity => activity
        && !activity.dismissed
        && !activity.is_recommendation);
}

/**
 * Assess whether each visible client parent price covers its canonical Stage 4
 * pricing floor. Work Method cost is diagnostic only; it must not override the
 * later committed scope projection.
 */
export function assessQuotePricingReadiness({
    scope = [],
    projectWMS = null,
    isGstInclusive = true,
}: QuotePricingReadinessInput = {}): QuotePricingReadiness {
    const items = Array.isArray(scope) ? scope.filter(Boolean) as UnknownRecord[] : [];
    const pricingLedger = buildStage4PricingLedger(items, isGstInclusive !== false);
    const quoteRevenueExGst = pricingLedger.quoteRevenueExGst;
    const pricingFloorExGst = pricingLedger.pricingFloorExGst;
    const scopeCostExGst = pricingFloorExGst;
    const workMethodCostExGst = roundCents(coreWorkMethodActivities(projectWMS).reduce(
        (sum, activity) => sum + costBreakdownTotal(activity),
        0,
    ));

    const knownCostExGst = pricingFloorExGst;
    const costSource = pricingFloorExGst > 0 ? 'scope' : 'none';
    const marginAmount = pricingLedger.pricingHeadroom;
    const shortfall = pricingLedger.shortfall;
    const hasKnownCost = pricingFloorExGst > 0;
    const pendingInclusionPricingReviewCount = items.reduce((count, item) => (
        count + (Array.isArray(item?.inclusions)
            ? item.inclusions.filter(requiresInclusionPricingRevalidation).length
            : 0)
    ), 0);
    const pendingInclusionPricingReview = pendingInclusionPricingReviewCount > 0;
    const allowanceOverAllocation = items.reduce((result, item) => {
        const excess = allowanceExcessForItem(item);
        if (excess <= 0) return result;
        return {
            parentCount: result.parentCount + 1,
            excess: roundCents(result.excess + excess),
        };
    }, { parentCount: 0, excess: 0 });
    const overAllocatedAllowanceParentCount = allowanceOverAllocation.parentCount;
    const overAllocatedAllowanceExcess = allowanceOverAllocation.excess;
    const overAllocatedAllowance = overAllocatedAllowanceParentCount > 0;
    // OPERATOR DECISION (Peter 2026-07-29): `lossMaking` fires on MATERIAL
    // findings only (> $50 AND > 1% per item) and drives WARNINGS, never hard
    // blocks. Cent-noise stays visible in per-item ledger displays via
    // `belowFloor` but raises nothing.
    const lossMaking = pricingLedger.materialUnderpricedItemCount > 0;

    return {
        quoteRevenueExGst,
        pricingFloorExGst,
        pricingFloorSource: hasKnownCost ? 'stage4-scope-ledger' : 'none',
        rateBookChargeOutIncGst: pricingLedger.rateBookChargeOutIncGst,
        rateBookChargeOutExGst: pricingLedger.rateBookChargeOutExGst,
        unmatchedCostBaseExGst: pricingLedger.unmatchedCostBaseExGst,
        pricingHeadroom: pricingLedger.pricingHeadroom,
        underpricedItemCount: pricingLedger.underpricedItemCount,
        pricingLedger,
        actualBuilderCostExGst: pricingLedger.actualBuilderCostExGst,
        actualMarginAmount: pricingLedger.actualMarginAmount,
        actualMarginPercent: pricingLedger.actualMarginPercent,
        actualCostCoverage: pricingLedger.actualCostCoverage,
        workMethodCostExGst,
        scopeCostExGst,
        knownCostExGst,
        costSource,
        marginAmount,
        marginPercent: hasKnownCost
            ? Math.round((marginAmount / knownCostExGst) * 10_000) / 100
            : null,
        shortfall,
        hasKnownCost,
        lossMaking,
        pendingInclusionPricingReviewCount,
        pendingInclusionPricingReview,
        overAllocatedAllowanceParentCount,
        overAllocatedAllowanceExcess,
        overAllocatedAllowance,
        // `blocked` is now DATA-INTEGRITY only (unpriced moved allowances,
        // allowances exceeding their parent price — internally contradictory
        // data). Price-vs-floor findings NEVER block: the operator ruled
        // "i dont want a block at all, just a warning" (2026-07-29). Consumers
        // that want the warning read `lossMaking`.
        blocked: pendingInclusionPricingReview || overAllocatedAllowance,
    };
}

/**
 * Whether cost coverage asks the builder to review pricing: some cost is recorded but not all of it
 * (`partial`), or cost lines are missing their rates (`unknown`). A quote with no recorded cost at
 * all (`none`, most quotes priced by QuoteMaker) has nothing to complete, so it is not a review
 * reason: Peter's ruling of 2026-11-04 ("Yes, stop flagging"). The Stage 4 Pricing tab, its card
 * and the Assistant's readiness check read this one rule.
 */
export function costCoverageNeedsReview(coverage: ActualCostCoverage | null | undefined): boolean {
    return coverage === 'partial' || coverage === 'unknown';
}

export function formatQuotePricingShortfall(readiness: QuotePricingReadiness): string {
    const format = (value: number) => value.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    const count = readiness.pricingLedger?.materialUnderpricedItemCount
        || readiness.underpricedItemCount
        || 1;
    return `Client pricing is below the canonical Stage 4 pricing floor by ${format(readiness.shortfall)} across ${count} scope item${count === 1 ? '' : 's'}.`;
}

export interface QuotePricingShortfallExplanation {
    /** One line naming the amount at stake. Suitable for a dialog title. */
    headline: string;
    /** The arithmetic in plain words, naming the worst item where there is one. */
    detail: string;
    /** What the operator can actually do about it. */
    remedy: string;
}

/**
 * Operator-facing explanation of a below-floor quote.
 *
 * `formatQuotePricingShortfall` above stays as-is: it is read by batch
 * failure reasons, Assistant output and delivery-refusal toasts, where the
 * internal vocabulary ("canonical pricing floor") is the right register.
 * This one is read by a builder in the middle of pricing a job, so it names
 * the specific item, shows both numbers that produce the gap, and says what
 * to do next.
 *
 * Wording note: the floor is `rate-book charge-out + unmatched cost base`, so
 * it is not always literally cost-to-business. The copy says "costed at"
 * rather than "costs you", which is true whichever way the floor is composed.
 */
export function explainQuotePricingShortfall(readiness: QuotePricingReadiness): QuotePricingShortfallExplanation {
    const money = (value: number) => value.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const below = (readiness.pricingLedger?.items || [])
        .filter(item => item.belowFloor)
        .slice()
        .sort((a, b) => b.shortfall - a.shortfall);
    const worst = below[0];
    const count = below.length || readiness.underpricedItemCount || 1;

    const headline = `This quote is ${money(readiness.shortfall)} under its costings`;

    let detail: string;
    if (count === 1 && worst) {
        detail = `"${worst.title}" is priced at ${money(worst.revenueExGst)} ex GST, but its Stage 4 costings total ${money(worst.pricingFloorExGst)} ex GST. That leaves you ${money(worst.shortfall)} short on this job.`;
    } else if (worst) {
        detail = `${count} scope items are priced below what they are costed at, ${money(readiness.shortfall)} in total. The largest gap is "${worst.title}", short by ${money(worst.shortfall)}.`;
    } else {
        detail = `Your price of ${money(readiness.quoteRevenueExGst)} ex GST is below the ${money(readiness.pricingFloorExGst)} ex GST this scope is costed at, leaving you ${money(readiness.shortfall)} short across ${count} scope item${count === 1 ? '' : 's'}.`;
    }

    const remedy = count === 1
        ? 'To fix it, raise that item\'s price, or open its cost breakdown if the costs look too high.'
        : 'To fix it, raise the affected item prices, or open their cost breakdowns if the costs look too high.';

    return { headline, detail, remedy };
}

export function formatAllowanceOverAllocation(readiness: QuotePricingReadiness): string {
    const count = readiness.overAllocatedAllowanceParentCount;
    const excess = readiness.overAllocatedAllowanceExcess.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${count} scope item${count === 1 ? '' : 's'} disclose explicit allowances above the parent item total by ${excess} in the active GST basis.`;
}
